import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from '../lib/safeToast';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { getApiErrorMessage } from '../lib/utils';
import { Eye, EyeOff, ArrowRight, Loader2, CheckCircle } from 'lucide-react';

const Register = () => {
  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [serverCode, setServerCode] = useState('');
  const { register, verifyEmail } = useAuth();
  const navigate = useNavigate();

  const handleRegister = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    try {
      const result = await register(email, password, name);
      setServerCode(result.verification_code);
      toast.success('Account created! Please verify your email.');
      setStep(2);
    } catch (error) {
      toast.error(getApiErrorMessage(error.response?.data?.detail, 'Registration failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await verifyEmail(email, verificationCode);
      toast.success('Email verified! You can now log in.');
      setStep(3);
    } catch (error) {
      toast.error('Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="register-page">
      {/* Left Panel - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-12">
            <img src="/logo.png" alt="PayZen" className="w-12 h-12 rounded-xl object-contain" />
            <div>
              <h1 className="font-bold text-xl  text-[#1C472B]">PayZen</h1>
              <p className="text-xs text-slate-500">Merchant Hub</p>
            </div>
          </div>

          {/* Step Indicator */}
          <div className="flex items-center gap-2 mb-8">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                    step >= s
                      ? 'bg-[#1C472B] text-white'
                      : 'bg-slate-100 text-slate-400'
                  }`}
                >
                  {step > s ? <CheckCircle className="w-5 h-5" /> : s}
                </div>
                {s < 3 && (
                  <div
                    className={`w-12 h-0.5 ${
                      step > s ? 'bg-[#1C472B]' : 'bg-slate-200'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>

          {step === 1 && (
            <>
              <h2 className="text-3xl font-extrabold text-[#0F172A]  mb-2">
                Create Account
              </h2>
              <p className="text-slate-500 mb-8">
                Join thousands of merchants managing their payments
              </p>

              <form onSubmit={handleRegister} className="space-y-5">
                <div>
                  <label className="input-label">Full Name</label>
                  <Input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="John Doe"
                    className="input-field"
                    required
                    data-testid="register-name"
                  />
                </div>

                <div>
                  <label className="input-label">Email Address</label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="input-field"
                    required
                    data-testid="register-email"
                  />
                </div>

                <div>
                  <label className="input-label">Password</label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Min. 8 characters"
                      className="input-field pr-12"
                      required
                      autoComplete="new-password"
                      data-testid="register-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="input-label">Confirm Password</label>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your password"
                    className="input-field"
                    required
                    autoComplete="new-password"
                    data-testid="register-confirm-password"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 text-base rounded-full font-semibold bg-[#1C472B] hover:bg-[#143620] active:bg-[#0F2818] text-white shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  disabled={loading}
                  data-testid="register-submit"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      Create Account
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
              </form>

              <p className="text-center mt-8 text-slate-500">
                Already have an account?{' '}
                <Link to="/login" className="text-[#1C472B] font-semibold hover:underline">
                  Sign in
                </Link>
              </p>
            </>
          )}

          {step === 2 && (
            <>
              <h2 className="text-3xl font-extrabold text-[#0F172A]  mb-2">
                Verify Email
              </h2>
              <p className="text-slate-500 mb-4">
                Enter the verification code sent to <strong>{email}</strong>
              </p>

              {serverCode && (
                <div className="bg-[#E6F7F3] border border-[#A5E0D2] rounded-xl p-4 mb-6">
                  <p className="text-sm text-[#1C472B]">
                    <strong>Demo Mode:</strong> Your verification code is{' '}
                    <span className="font-mono font-bold">{serverCode}</span>
                  </p>
                </div>
              )}

              <form onSubmit={handleVerify} className="space-y-6">
                <div>
                  <label className="input-label">Verification Code</label>
                  <Input
                    type="text"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    className="input-field text-center text-2xl tracking-widest font-mono"
                    maxLength={6}
                    required
                    data-testid="verify-code"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 text-base rounded-full font-semibold bg-[#1C472B] hover:bg-[#143620] active:bg-[#0F2818] text-white shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  disabled={loading || verificationCode.length !== 6}
                  data-testid="verify-submit"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    'Verify Email'
                  )}
                </Button>
              </form>
            </>
          )}

          {step === 3 && (
            <div className="text-center">
              <div className="w-20 h-20 bg-[#E6F7F3] rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-[#1C472B]" />
              </div>
              <h2 className="text-3xl font-extrabold text-[#0F172A]  mb-2">
                All Set!
              </h2>
              <p className="text-slate-500 mb-8">
                Your account has been verified successfully.
              </p>
              <Button
                onClick={() => navigate('/login')}
                className="w-full h-12 text-base rounded-full font-semibold bg-[#1C472B] hover:bg-[#143620] active:bg-[#0F2818] text-white shadow-sm transition-colors flex items-center justify-center gap-2"
                data-testid="go-to-login"
              >
                Continue to Login
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Right Panel - Image */}
      <div
        className="hidden lg:block lg:w-1/2 bg-cover bg-center"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1571176242081-bf093bb3a10d?crop=entropy&cs=srgb&fm=jpg&q=85')`,
        }}
      >
        <div className="h-full w-full bg-[#1C472B]/80 flex items-center justify-center p-12">
          <div className="text-center text-white max-w-lg">
            <h2 className="text-4xl font-extrabold  mb-4">
              Start Your Journey
            </h2>
            <p className="text-lg text-slate-300">
              Create your merchant account and unlock powerful payment management tools with PayZen.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
